import api from './index'
import type {
  ApiResponse,
  FormTemplateSummary,
  FormTemplateDetail,
  FormTemplateVersionDetail,
  FormTemplateDiffResult,
  FormTemplateExportData,
  FormSectionCreateInput,
  FormSectionUpdatePayload,
  FormFieldUpdatePayload,
  FormFieldOption,
  FormFieldType,
  FormLogicAction,
  FormLogicOperator,
  FormTemplateCreatePayload,
  FormTemplateUpdatePayload,
  FormTemplateVersionCreatePayload,
  FormTemplatePublishOptions,
} from '@/types'

interface FetchTemplateParams {
  scopeType?: string
  projectId?: string
  includeArchived?: boolean
  search?: string
}

export const getTemplates = async (
  params: FetchTemplateParams = {},
): Promise<ApiResponse<FormTemplateSummary[]>> => {
  const response = await api.get('/forms/templates', {
    params: {
      ...params,
      _cb: Date.now(),
    },
  })
  const payload = response.data?.data
  const templates = Array.isArray(payload)
    ? payload
    : payload?.templates ?? []
  return {
    success: response.data?.success ?? true,
    data: templates,
    message: response.data?.message,
  }
}

export const getTemplateDetail = async (
  templateId: string,
): Promise<ApiResponse<FormTemplateDetail>> => {
  const response = await api.get(`/forms/templates/${templateId}`, {
    params: { _cb: Date.now() },
  })
  return {
    success: response.data?.success ?? true,
    data: response.data?.data,
    message: response.data?.message,
  }
}

export const createTemplate = async (
  payload: FormTemplateCreatePayload,
): Promise<ApiResponse<FormTemplateDetail>> => {
  const response = await api.post('/forms/templates', payload)
  return {
    success: response.data.success,
    data: response.data.data,
    message: response.data.message,
  }
}

export const updateTemplate = async (
  templateId: string,
  payload: FormTemplateUpdatePayload,
): Promise<ApiResponse<FormTemplateSummary>> => {
  const response = await api.patch(`/forms/templates/${templateId}`, payload)
  return {
    success: response.data.success,
    data: response.data.data,
    message: response.data.message,
  }
}

export const deleteTemplate = async (
  templateId: string,
): Promise<ApiResponse> => {
  const response = await api.delete(`/forms/templates/${templateId}`)
  return response.data
}

export const createTemplateVersion = async (
  templateId: string,
  payload: FormTemplateVersionCreatePayload,
): Promise<ApiResponse<FormTemplateVersionDetail>> => {
  const response = await api.post(`/forms/templates/${templateId}/versions`, payload)
  return {
    success: response.data.success,
    data: response.data.data,
    message: response.data.message,
  }
}

export const publishTemplateVersion = async (
  templateId: string,
  versionId: string,
  options: FormTemplatePublishOptions,
): Promise<ApiResponse<FormTemplateVersionDetail>> => {
  const response = await api.post(
    `/forms/templates/${templateId}/versions/${versionId}/publish`,
    options,
  )
  return {
    success: response.data.success,
    data: response.data.data,
    message: response.data.message,
  }
}

export const diffTemplateVersions = async (
  templateId: string,
  versionId: string,
  compareTo: string,
): Promise<ApiResponse<FormTemplateDiffResult>> => {
  const response = await api.get(`/forms/templates/${templateId}/versions/${versionId}/diff`, {
    params: { compareTo },
  })
  return response.data
}

export const exportTemplateVersion = async (
  templateId: string,
  versionId: string,
): Promise<ApiResponse<FormTemplateExportData>> => {
  const response = await api.get(`/forms/templates/${templateId}/versions/${versionId}/export`)
  return response.data
}

export const importTemplate = async (
  payload: FormTemplateExportData,
): Promise<ApiResponse<{ template: FormTemplateSummary }>> => {
  const response = await api.post('/forms/templates/import', payload)
  return response.data
}

export const createSection = async (
  templateId: string,
  versionId: string,
  payload: FormSectionCreateInput,
): Promise<ApiResponse> => {
  const response = await api.post(`/forms/templates/${templateId}/versions/${versionId}/sections`, payload)
  return response.data
}

export const updateSection = async (
  templateId: string,
  versionId: string,
  sectionId: string,
  payload: FormSectionUpdatePayload,
): Promise<ApiResponse> => {
  const response = await api.patch(`/forms/templates/${templateId}/versions/${versionId}/sections/${sectionId}`, payload)
  return response.data
}

export const deleteSection = async (
  templateId: string,
  versionId: string,
  sectionId: string,
): Promise<ApiResponse> => {
  const response = await api.delete(`/forms/templates/${templateId}/versions/${versionId}/sections/${sectionId}`)
  return response.data
}

interface CreateFieldPayload {
  key: string
  label: string
  type: FormFieldType
  description?: string | null
  isRequired?: boolean
  isRepeatable?: boolean
  placeholder?: string | null
  helpText?: string | null
  defaultValue?: unknown
  metadata?: Record<string, unknown>
  category?: string | null
  subcategory?: string | null
  position?: number
  sectionId?: string | null
  options?: FormFieldOption[]
}

export const createField = async (
  templateId: string,
  versionId: string,
  payload: CreateFieldPayload,
): Promise<ApiResponse> => {
  const response = await api.post(`/forms/templates/${templateId}/versions/${versionId}/fields`, payload)
  return response.data
}

export const updateField = async (
  templateId: string,
  versionId: string,
  fieldId: string,
  payload: FormFieldUpdatePayload,
): Promise<ApiResponse> => {
  const response = await api.patch(`/forms/templates/${templateId}/versions/${versionId}/fields/${fieldId}`, payload)
  return response.data
}

export const deleteField = async (
  templateId: string,
  versionId: string,
  fieldId: string,
): Promise<ApiResponse> => {
  const response = await api.delete(`/forms/templates/${templateId}/versions/${versionId}/fields/${fieldId}`)
  return response.data
}

export interface CreateLogicRulePayload {
  triggerFieldId: string
  effectFieldId: string
  operator: FormLogicOperator
  action: FormLogicAction
  comparisonValue?: unknown
  position?: number
}

export interface UpdateLogicRulePayload {
  triggerFieldId?: string
  effectFieldId?: string
  operator?: FormLogicOperator
  action?: FormLogicAction
  comparisonValue?: unknown | null
  position?: number
}

export const createLogicRule = async (
  templateId: string,
  versionId: string,
  payload: CreateLogicRulePayload,
): Promise<ApiResponse> => {
  const response = await api.post(`/forms/templates/${templateId}/versions/${versionId}/logic-rules`, payload)
  return response.data
}

export const updateLogicRule = async (
  templateId: string,
  versionId: string,
  ruleId: string,
  payload: UpdateLogicRulePayload,
): Promise<ApiResponse> => {
  const response = await api.patch(
    `/forms/templates/${templateId}/versions/${versionId}/logic-rules/${ruleId}`,
    payload,
  )
  return response.data
}

export const deleteLogicRule = async (
  templateId: string,
  versionId: string,
  ruleId: string,
): Promise<ApiResponse> => {
  const response = await api.delete(
    `/forms/templates/${templateId}/versions/${versionId}/logic-rules/${ruleId}`,
  )
  return response.data
}

export const reorderLogicRules = async (
  templateId: string,
  versionId: string,
  orderedRuleIds: string[],
): Promise<ApiResponse> => {
  const response = await api.post(
    `/forms/templates/${templateId}/versions/${versionId}/logic-rules/reorder`,
    { orderedRuleIds },
  )
  return response.data
}
